import React, { useState, useEffect } from 'react';
import './App.css';
import { StudyProvider, useStudy } from './context/StudyContext';
import StudySetSelector from './components/StudySetSelector';
import CategorySelector from './components/CategorySelector';
import ModeSelector from './components/ModeSelector';
import StudySession from './components/StudySession';
import FileUpload from './components/FileUpload';

function AppContent() {
  const { getStudySetsSummary, createStudySession, createReviewSession } = useStudy();
  
  const [studySets, setStudySets] = useState([]);
  const [selectedStudySet, setSelectedStudySet] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [studySession, setStudySession] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [reviewQuestionIds, setReviewQuestionIds] = useState(null);

  // Update category progress in localStorage
  const updateCategoryProgress = (studySetId, categoryResults) => {
    const key = `progress_${studySetId}`;
    const stored = localStorage.getItem(key);
    const progress = stored ? JSON.parse(stored) : {};

    // Update progress for each category - use the better result
    Object.keys(categoryResults).forEach(category => {
      const result = categoryResults[category];
      const currentPercentage = result.attempted > 0 ? (result.correct / result.attempted) * 100 : 0;
      
      if (!progress[category]) {
        // New category - store the result
        progress[category] = { attempted: result.attempted, correct: result.correct };
      } else {
        // Existing category - keep the better percentage
        const existingPercentage = (progress[category].correct / progress[category].attempted) * 100;
        if (currentPercentage > existingPercentage) {
          progress[category] = { attempted: result.attempted, correct: result.correct };
        }
      }
    });

    localStorage.setItem(key, JSON.stringify(progress));
  };

  // Update mode completion in localStorage (per category)
  const updateModeCompletion = (studySetId, mode, percentage, categoryResults) => {
    // Only track if 100% completion
    if (percentage === 100 && categoryResults) {
      // Mark mode as complete for each category that was 100% correct
      Object.keys(categoryResults).forEach(categoryName => {
        const result = categoryResults[categoryName];
        const categoryPercentage = result.attempted > 0 ? (result.correct / result.attempted) * 100 : 0;
        
        if (categoryPercentage === 100) {
          const key = `mode_completion_${studySetId}_${categoryName}`;
          const stored = localStorage.getItem(key);
          const modeCompletion = stored ? JSON.parse(stored) : {};
          modeCompletion[mode] = true;
          localStorage.setItem(key, JSON.stringify(modeCompletion));
        }
      });
    }
  };

  useEffect(() => {
    loadStudySets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStudySets = () => {
    const summary = getStudySetsSummary();
    setStudySets(summary);
  };

  const handleStudySetSelect = (studySetId) => {
    setSelectedStudySet(studySetId);
    setSelectedCategories(null);
    setSelectedMode(null);
    setStudySession(null);
  };

  const handleCategorySelect = (categories) => {
    setSelectedCategories(categories);
  };

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    
    let session;
    if (reviewQuestionIds) {
      // Creating a review session for specific questions
      session = createReviewSession(selectedStudySet, mode, reviewQuestionIds);
    } else {
      // Creating a normal study session
      session = createStudySession(selectedStudySet, mode, 50, selectedCategories);
    }
    
    setStudySession(session);
    setReviewQuestionIds(null); // Clear review IDs after use
  };

  const handleBackToSets = () => {
    setSelectedStudySet(null);
    setSelectedCategories(null);
    setSelectedMode(null);
    setStudySession(null);
    setReviewQuestionIds(null);
  };

  const handleBackToCategories = () => {
    setSelectedCategories(null);
    setSelectedMode(null);
    setStudySession(null);
  };

  const handleBackToModes = () => {
    setSelectedMode(null);
    setStudySession(null);
  };

  const handleReviewMistakes = (incorrectQuestionIds) => {
    setReviewQuestionIds(incorrectQuestionIds);
    setSelectedMode(null);
    setStudySession(null);
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    loadStudySets();
  };

  return (
    <div className="App">
      <header className="app-header">
        <h1>📚 StudyMaster Pro</h1>
        <p className="tagline">Master any subject with adaptive learning</p>
      </header>

      <main className="app-main">
        {!selectedStudySet && !showUpload && (
          <>
            <StudySetSelector
              studySets={studySets}
              onSelect={handleStudySetSelect}
            />
            <button 
              className="upload-toggle-btn"
              onClick={() => setShowUpload(true)}
            >
              + Upload New Study Set
            </button>
          </>
        )}

        {showUpload && (
          <FileUpload
            onSuccess={handleUploadSuccess}
            onCancel={() => setShowUpload(false)}
          />
        )}

        {selectedStudySet && selectedCategories === null && (
          <CategorySelector
            studySetId={selectedStudySet}
            onSelect={handleCategorySelect}
            onBack={handleBackToSets}
          />
        )}

        {selectedStudySet && selectedCategories && !selectedMode && (
          <ModeSelector
            onSelect={handleModeSelect}
            onBack={handleBackToCategories}
          />
        )}

        {studySession && (
          <StudySession
            session={studySession}
            studySetId={selectedStudySet}
            onBack={handleBackToModes}
            onComplete={handleBackToSets}
            onReviewMistakes={handleReviewMistakes}
            onCategoryProgressUpdate={(results) => updateCategoryProgress(selectedStudySet, results)}
            onModeCompletion={(mode, percentage, categoryResults) => updateModeCompletion(selectedStudySet, mode, percentage, categoryResults)}
          />
        )}
      </main>

      <footer className="app-footer">
        <p>Built with React | Customize your learning experience</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <StudyProvider>
      <AppContent />
    </StudyProvider>
  );
}

export default App;
